import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { SetAuthAction, ValidateTokenAction } from '@tsin-core/actions/auth.action';
import { AuthState } from '@tsin-core/states/auth.state';
import { ConfigService } from '@tsin-core/services/config.service';
import { determineAuthStatus } from '@tsin-shared/utils/utils';

/**
 * SsoCallbackComponent — handles the post-login redirect from auth-ui.
 *
 * Mirrors the callback pattern used by PRO / LMS / WWUS / MMI frontends.
 *
 * Flow / Flux :
 * 1. auth-ui sets an `id_token` cookie on .stage.tsin.ca after Cognito login.
 * 2. User is redirected to /auth/callback (this component).
 * 3. Component reads the cookie, calls POST /auth/cognito/token-exchange
 *    (service=LEARNING) to obtain a Learning JWT signed with the shared secret.
 * 4. SetAuthAction patches NGXS state with the minimal user + token.
 * 5. ValidateTokenAction calls GET /user/me on the Learning backend; because
 *    the backend now uses the same JWT_SECRET as the Auth backend (injected
 *    via Secrets Manager by CDK), it can verify the token and return the
 *    REAL user with their REAL role from the Learning MongoDB.
 * 6. determineAuthStatus(realUser) selects the correct dashboard route.
 * 7. Fallback: if /user/me fails (network error etc.) the minimal role from
 *    step 3 is used to route the user.
 *
 * Composant SsoCallback — gère la redirection post-connexion depuis auth-ui.
 * Miroir du pattern callback utilisé par PRO / LMS / WWUS / MMI.
 */
@Component({
  selector: 'app-sso-callback',
  standalone: true,
  imports: [CommonModule],
  // No TranslateModule — learning frontend does not use ngx-translate yet.
  template: `
    <div class="callback-container">
      <div class="callback-content">
        @if (!error) {
          <div class="spinner" aria-hidden="true"></div>
          <h2>{{ message }}</h2>
        }
        @if (error) {
          <div class="error-box" role="alert">
            <p>{{ error }}</p>
            <button (click)="retryLogin()">
              Retry&nbsp;/&nbsp;Réessayer
            </button>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .callback-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: #f5f5f5;
    }
    .callback-content {
      background: white;
      padding: 3rem;
      border-radius: 12px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.08);
      text-align: center;
      max-width: 400px;
      width: 90%;
    }
    .spinner {
      border: 4px solid #e0e0e0;
      border-top: 4px solid #1976d2;
      border-radius: 50%;
      width: 48px;
      height: 48px;
      animation: spin 0.9s linear infinite;
      margin: 0 auto 1.5rem;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    h2 { color: #333; font-size: 1.25rem; margin-bottom: 0.5rem; }
    .error-box {
      padding: 1rem;
      background: #fff3f3;
      border-left: 4px solid #e53935;
      border-radius: 6px;
    }
    .error-box p { color: #c62828; margin-bottom: 1rem; }
    button {
      padding: 0.6rem 1.4rem;
      background: #1976d2;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 0.95rem;
    }
    button:hover { background: #1565c0; }
  `]
})
export class SsoCallbackComponent implements OnInit {
  message = 'Authenticating… / Authentification en cours…';
  error: string | null = null;

  constructor(
    private router:        Router,
    private http:          HttpClient,
    private store:         Store,
    private configService: ConfigService,
  ) {}

  ngOnInit(): void {
    this.handleCallback();
  }

  // -------------------------------------------------------------------------
  // Core callback handler
  // -------------------------------------------------------------------------

  private async handleCallback(): Promise<void> {
    const idToken = this.getCookie('id_token');

    if (!idToken) {
      this.error =
        'No authentication token found. Please try logging in again. / ' +
        'Aucun jeton d\'authentification trouvé. Veuillez vous reconnecter.';
      return;
    }

    try {
      this.message = 'Verifying credentials… / Vérification des informations…';

      // ── Step 1: exchange Cognito id_token for a Learning JWT ──────────────
      // Échange le cookie Cognito contre un JWT Learning via le backend Auth.
      const authServiceUrl = this.configService.getAuthServiceUrl();
      const response: any = await this.http
        .post(`${authServiceUrl}/api/auth/cognito/token-exchange`, {
          cognitoIdToken: idToken,
          service: 'LEARNING',
        })
        .toPromise();

      const token        = response?.data?.token        ?? response?.token;
      const refreshToken = response?.data?.refreshToken ?? response?.refreshToken ?? '';
      const success      = response?.success            ?? !!token;

      if (!success || !token) {
        throw new Error(
          response?.message ?? 'Token exchange failed / Échec de l\'échange de jeton',
        );
      }

      // ── Step 2: extract minimal role from Auth backend response ───────────
      // Extraire le rôle minimal depuis la réponse du backend Auth.
      //
      // Priority (highest → lowest) / Priorité (plus haute → plus basse) :
      //   1. auth DB profiles: any profile with role 'superAdmin'
      //   2. serviceRoles array (may be propagated from other services in DB)
      //   3. LEARNING-specific profile role
      //   4. First role in the roles array
      //   5. Default: 'user'
      const rawUser  = response?.data?.user ?? response?.user ?? {};
      const profiles: any[]  = Array.isArray(rawUser?.profiles) ? rawUser.profiles : [];
      const allRoles: string[] = Array.isArray(rawUser?.roles)   ? rawUser.roles   : [];

      const isSuperAdmin =
        profiles.some((p: any) => p.role === 'superAdmin') ||
        allRoles.includes('superAdmin');

      const learningProfile = profiles.find(
        (p: any) => p.project === 'LEARNING' || p.project === 'learning',
      );
      const fallbackRole = isSuperAdmin
        ? 'superAdmin'
        : (learningProfile?.role ?? allRoles[0] ?? 'user');

      console.log('🔍 SSO role extraction (auth-backend):', {
        isSuperAdmin, learningProfileRole: learningProfile?.role, allRoles, fallbackRole,
      });

      // Build a minimal UserModel for SetAuthAction.
      // Construit un UserModel minimal pour SetAuthAction.
      const minimalUser = {
        id:    rawUser?.id ?? rawUser?._id ?? null,
        email: rawUser?.email ?? null,
        role:  fallbackRole,
      };

      // ── Step 3: SetAuthAction — patch NGXS state with token + minimal user ─
      // SetAuthAction — mettre à jour l'état NGXS avec le jeton et l'utilisateur minimal.
      await this.store
        .dispatch(new SetAuthAction({ token, refreshToken: refreshToken || null, user: minimalUser }))
        .toPromise();

      // ── Step 4: ValidateTokenAction — GET /user/me on Learning backend ─────
      // Calls the learning backend which can now verify the JWT because the
      // shared JWT_SECRET is injected by CDK.  Returns the REAL user object
      // (with real role + examType) from the Learning MongoDB.
      //
      // ValidateTokenAction — GET /user/me sur le backend Learning.
      // Le backend peut maintenant vérifier le JWT car le JWT_SECRET partagé
      // est injecté par CDK. Retourne l'utilisateur RÉEL depuis la MongoDB Learning.
      this.message = 'Loading your profile… / Chargement de votre profil…';
      await this.store
        .dispatch(new ValidateTokenAction({ token, refreshToken: refreshToken || '' }))
        .toPromise();

      // ── Step 5: Read the (now real) user from NGXS state and navigate ─────
      // Lire l'utilisateur (maintenant réel) depuis l'état NGXS et naviguer.
      // AuthState.getCurrentUser returns the user object set by ValidateTokenAction
      // (from learning backend /user/me) or the minimal user from SetAuthAction.
      // Cast to UserModel — the selector may return a partial object but
      // determineAuthStatus() only reads .role and .examType, both optional.
      // Cast vers UserModel — le sélecteur peut retourner un objet partiel mais
      // determineAuthStatus() ne lit que .role et .examType, tous deux optionnels.
      const stateUser = this.store.selectSnapshot(AuthState.getCurrentUser);
      const realUser  = (stateUser ?? minimalUser) as any;

      console.log('🔍 SSO final user (after /user/me):', {
        role: realUser?.role, examType: realUser?.examType,
      });

      // determineAuthStatus() returns /user/${examType} for non-admin roles;
      // replace /user/undefined with the safe default /user.
      // determineAuthStatus() retourne /user/${examType} pour les non-admins ;
      // remplacer /user/undefined par le repli sécurisé /user.
      let route = determineAuthStatus(realUser as any);
      if (!route || route.includes('undefined')) route = '/user';

      console.log('✅ SSO login complete — navigating to', route);
      this.router.navigate([route]);

    } catch (err: any) {
      console.error('❌ SSO callback error:', err);
      this.error =
        (err?.error?.message ?? err?.message ?? 'Authentication failed.') +
        ' / Échec de l\'authentification.';
    }
  }

  // -------------------------------------------------------------------------
  // Public helpers
  // -------------------------------------------------------------------------

  /**
   * Redirect to auth-ui login page to retry.
   * Preserves the /fr locale so French users return to the French login page.
   *
   * Redirige vers la page de connexion auth-ui pour réessayer.
   * Préserve le préfixe /fr pour que les francophones restent sur la page française.
   */
  retryLogin(): void {
    const authUiUrl    = this.configService.getAuthUiUrl();
    const isFr         = window.location.pathname.startsWith('/fr');
    const callbackPath = isFr ? '/fr/auth/callback' : '/auth/callback';
    const loginPath    = isFr ? '/fr/login' : '/login';
    const returnTo     = encodeURIComponent(`${window.location.origin}${callbackPath}`);
    window.location.href = `${authUiUrl}${loginPath}?returnTo=${returnTo}`;
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private getCookie(name: string): string | null {
    const match = document.cookie.match(
      new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'),
    );
    return match ? decodeURIComponent(match[1]) : null;
  }
}

